import React, { type ComponentType } from "react";
import { type ElementType } from "domelementtype";
import { twMerge } from "tailwind-merge";
import { isNullOrUndefined } from "@utils/common";

export type TBadgeColor =
  | "grey"
  | "blue"
  | "green"
  | "red"
  | "purple"
  | "violet"
  | "yellow"
  | "orange"
  | "dark_blue"
  | "budget_green"
  | "budget_purple"
  | "budget_blue";

export interface IBadgeProps {
  /**
   * Badge color scheme.
   */
  color?: TBadgeColor;
  /**
   * Badge variant.
   */
  variant?: "default" | "dense-bubble" | "bubble" | "rounded";
  /**
   * Badge size.
   */
  size?: "small" | "medium" | "large";
  /**
   * Icon element.
   */
  icon?: ComponentType | ElementType | string;
  /**
   * Badge className.
   */
  className?: string;
  /**
   * Badge icon className.
   */
  iconClassName?: string;
  /**
   * Badge as button callback;
   */
  onClick?: () => void;
}

const getBadgeClassName = ({
  color,
  variant,
  size,
  className,
  onClick,
}: Partial<IBadgeProps>) =>
  twMerge(
    "flex-shrink-0 inline-flex w-fit items-center justify-center whitespace-nowrap gap-2 h-min font-medium",
    !isNullOrUndefined(onClick) &&
      "focus:ring-4 active:ring-4 active:ring-opacity-10 focus:ring-opacity-10 outline-none",
    color === "grey" && "bg-grey-light text-base-black",
    color === "blue" && "bg-paleSky-light text-blue-dark",
    color === "dark_blue" && "bg-budget-darkBlue-base text-blue-dark",
    color === "green" && "bg-green-light text-green-dark",
    color === "red" && "bg-red-light text-red-dark",
    color === "purple" && "bg-purple-hover text-purple-dark",
    color === "violet" && "bg-violet-light text-violet-dark",
    color === "yellow" && "bg-yellow-light text-yellow-dark",
    color === "orange" && "bg-orange-light text-orange-dark",
    color === "budget_green" && "bg-budget-green-base",
    color === "budget_purple" && "bg-budget-purple-base",
    color === "budget_blue" && "bg-budget-blue-base",
    color === "grey" &&
      !isNullOrUndefined(onClick) &&
      "hover:bg-grey-hover focus:ring-grey-dark active:ring-grey-dark",
    color === "blue" &&
      !isNullOrUndefined(onClick) &&
      "hover:bg-blue-hover focus:ring-paleSky-dark active:ring-paleSky-dark",
    color === "dark_blue" &&
      !isNullOrUndefined(onClick) &&
      "hover:bg-blue-hover focus:ring-blue-dark active:ring-blue-dark",
    color === "green" &&
      !isNullOrUndefined(onClick) &&
      "hover:bg-green-hover focus:ring-green-dark active:ring-green-dark",
    color === "red" &&
      !isNullOrUndefined(onClick) &&
      "hover:bg-red-hover focus:ring-red-dark active:ring-red-dark",
    color === "purple" &&
      !isNullOrUndefined(onClick) &&
      "hover:bg-purple-hover focus:ring-purple-dark active:ring-purple-dark",
    color === "violet" &&
      !isNullOrUndefined(onClick) &&
      "hover:bg-violet-hover focus:ring-violet-dark active:ring-violet-dark",
    color === "yellow" &&
      !isNullOrUndefined(onClick) &&
      "hover:bg-yellow-hover focus:ring-yellow-dark active:ring-yellow-dark",
    color === "orange" &&
      !isNullOrUndefined(onClick) &&
      "hover:bg-orange-hover focus:ring-orange-dark active:ring-orange-dark",
    variant === "default" && "bg-transparent",
    variant === "default" && "text-base leading-6",
    (variant === "dense-bubble" || variant === "bubble") &&
      size === "small" &&
      "text-xs py-0.5 px-2 leading-[18px]",
    (variant === "dense-bubble" || variant === "bubble") &&
      size === "medium" &&
      "text-sm py-0.5 px-2 leading-5",
    (variant === "dense-bubble" || variant === "bubble") &&
      size === "large" &&
      "text-sm py-1 px-3 leading-5",
    variant === "dense-bubble" && "font-medium",
    variant === "rounded" && size === "small" && "p-2",
    variant === "rounded" && size === "medium" && "p-3.5",
    variant === "rounded" && size === "large" && "p-3.5",
    (variant === "dense-bubble" || variant === "bubble") && "rounded-2xl",
    variant === "rounded" && "rounded-full",
    className,
  );

const getIconClassName = ({
  color,
  variant,
  size,
  iconClassName,
}: Partial<IBadgeProps>) =>
  twMerge(
    "flex-shrink-0",
    color === "grey" && "text-base-black",
    color === "blue" && "text-blue-dark",
    color === "green" && "text-green-dark",
    color === "red" && "text-red-dark",
    color === "purple" && "text-purple-dark",
    color === "violet" && "text-violet-dark",
    color === "yellow" && "text-yellow-dark",
    color === "orange" && "text-orange-dark",
    variant === "default" && "size-4",
    variant === "dense-bubble" && size === "small" && "size-2",
    variant === "dense-bubble" && size === "medium" && "size-2",
    variant === "dense-bubble" && size === "large" && "size-2",
    variant === "bubble" && size === "small" && "size-3",
    variant === "bubble" && size === "medium" && "size-3",
    variant === "bubble" && size === "large" && "size-3",
    variant === "rounded" && size === "small" && "size-4",
    variant === "rounded" && size === "medium" && "size-5",
    variant === "rounded" && size === "large" && "size-6",
    iconClassName,
  );

export const Badge: React.FC<React.PropsWithChildren<IBadgeProps>> = ({
  color = "grey",
  size = "small",
  variant = "default",
  icon,
  className,
  iconClassName,
  onClick,
  children,
}) => {
  return React.createElement(
    !isNullOrUndefined(onClick) ? "button" : "span",
    {
      className: getBadgeClassName({
        color,
        variant,
        size,
        className,
        onClick,
      }),
      ...(!isNullOrUndefined(onClick) ? { onClick } : {}),
    },
    icon
      ? React.createElement(icon, {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          className: getIconClassName({ color, variant, size, iconClassName }),
        })
      : null,
    children && variant !== "rounded" ? (
      <span className="truncate">{children}</span>
    ) : null,
  );
};
